import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type SectionType =
  | "hero"
  | "media_text_split"
  | "full_bleed_media"
  | "gallery"
  | "pull_quote"
  | "stats"
  | "cta"
  | "accordion_inline";

export type CategoryRow = {
  id: string;
  label: string;
  short: string;
  position: number;
  kind: "sections" | "page";
  slug: string | null;
  hero_media_url: string | null;
  hero_media_type: "image" | "video" | null;
  seo_title: string | null;
  seo_description: string | null;
};

export type SectionRow = {
  id: string;
  category_id: string;
  position: number;
  type: SectionType;
  data: Record<string, any>;
  hidden: boolean;
};

function publicClient() {
  return createClient(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { persistSession: false } },
  );
}

const CAT_COLS =
  "id,label,short,position,kind,slug,hero_media_url,hero_media_type,seo_title,seo_description";
const SEC_COLS = "id,category_id,position,type,data,hidden";

export const getCategoryTree = createServerFn({ method: "GET" }).handler(
  async (): Promise<{
    categories: CategoryRow[];
    sections: SectionRow[];
    visibility: { category_id: string; experience_id: string }[];
  }> => {
    const sb = publicClient();
    const [catsRes, secsRes, visRes] = await Promise.all([
      sb.from("categories").select(CAT_COLS).order("position", { ascending: true }),
      sb
        .from("category_sections")
        .select(SEC_COLS)
        .eq("hidden", false)
        .order("position", { ascending: true }),
      sb.from("category_experiences_v2").select("category_id,experience_id"),
    ]);
    if (catsRes.error) throw new Error(catsRes.error.message);
    if (secsRes.error) throw new Error(secsRes.error.message);
    if (visRes.error) throw new Error(visRes.error.message);
    return {
      categories: (catsRes.data ?? []) as CategoryRow[],
      sections: (secsRes.data ?? []) as SectionRow[],
      visibility: (visRes.data ?? []) as { category_id: string; experience_id: string }[],
    };
  },
);

// --- Admin: categories ---

export const listCategoriesAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<CategoryRow[]> => {
    const { data, error } = await context.supabase
      .from("categories")
      .select(CAT_COLS)
      .order("position", { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? []) as CategoryRow[];
  });

const updateCategorySchema = z.object({
  id: z.string().min(1).max(100),
  label: z.string().min(1).max(120),
  short: z.string().min(1).max(40),
  kind: z.enum(["sections", "page"]),
  slug: z.string().max(60).regex(/^[a-z0-9-]*$/).nullable(),
  hero_media_url: z.string().url().max(2000).nullable(),
  hero_media_type: z.enum(["image", "video"]).nullable(),
  seo_title: z.string().max(200).nullable(),
  seo_description: z.string().max(500).nullable(),
});

export const updateCategoryV2 = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => updateCategorySchema.parse(input))
  .handler(async ({ data, context }) => {
    const { id, ...rest } = data;
    const { error } = await context.supabase
      .from("categories")
      .update({ ...rest, updated_at: new Date().toISOString() })
      .eq("id", id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

const reorderCatsSchema = z.object({
  ordered_ids: z.array(z.string().min(1).max(100)).min(1).max(50),
  });

// --- Admin: add / delete / duplicate / visibility list ---

function slugify(s: string) {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

const addCategorySchema = z.object({
  label: z.string().min(1).max(120),
  short: z.string().min(1).max(40),
  kind: z.enum(["sections", "page"]).default("sections"),
});

export const addCategoryV2 = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => addCategorySchema.parse(input))
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    const base = slugify(data.label) || "category";
    let id = base;
    for (let i = 2; i < 50; i++) {
      const { data: exists } = await sb.from("categories").select("id").eq("id", id).maybeSingle();
      if (!exists) break;
      id = `${base}-${i}`;
    }
    const { data: maxRow } = await sb
      .from("categories")
      .select("position")
      .order("position", { ascending: false })
      .limit(1);
    const nextPos = ((maxRow?.[0]?.position ?? -1) as number) + 1;
    const { error } = await sb.from("categories").insert({
      id,
      label: data.label,
      short: data.short,
      position: nextPos,
      kind: data.kind,
      slug: data.kind === "page" ? id : null,
    });
    if (error) throw new Error(error.message);
    // Default: visible in all existing experiences
    const { data: exps } = await sb.from("experiences").select("id");
    if (exps && exps.length > 0) {
      await sb
        .from("category_experiences_v2")
        .insert(exps.map((e) => ({ category_id: id, experience_id: e.id as string })));
    }
    return { ok: true, id };
  });

const deleteCategorySchema = z.object({ id: z.string().min(1).max(100) });

export const deleteCategoryV2 = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => deleteCategorySchema.parse(input))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("categories").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

const duplicateCategorySchema = z.object({ id: z.string().min(1).max(100) });

export const duplicateCategoryV2 = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => duplicateCategorySchema.parse(input))
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    const { data: src, error: srcErr } = await sb
      .from("categories")
      .select(CAT_COLS)
      .eq("id", data.id)
      .single();
    if (srcErr || !src) throw new Error(srcErr?.message ?? "Not found");
    const base = `${src.id}-copy`;
    let newId = base;
    for (let i = 2; i < 50; i++) {
      const { data: exists } = await sb.from("categories").select("id").eq("id", newId).maybeSingle();
      if (!exists) break;
      newId = `${base}-${i}`;
    }
    const { data: maxRow } = await sb
      .from("categories")
      .select("position")
      .order("position", { ascending: false })
      .limit(1);
    const nextPos = ((maxRow?.[0]?.position ?? -1) as number) + 1;
    const { error: insErr } = await sb.from("categories").insert({
      id: newId,
      label: `${src.label} (copy)`,
      short: src.short,
      position: nextPos,
      kind: src.kind,
      slug: src.kind === "page" ? newId : null,
      hero_media_url: src.hero_media_url,
      hero_media_type: src.hero_media_type,
      seo_title: src.seo_title,
      seo_description: src.seo_description,
    });
    if (insErr) throw new Error(insErr.message);
    // Copy sections
    const { data: secs } = await sb
      .from("category_sections")
      .select("position,type,data,hidden")
      .eq("category_id", data.id)
      .order("position", { ascending: true });
    if (secs && secs.length > 0) {
      const rows = secs.map((s) => ({
        category_id: newId,
        position: s.position,
        type: s.type,
        data: s.data,
        hidden: s.hidden,
      }));
      const { error: secErr } = await sb.from("category_sections").insert(rows);
      if (secErr) throw new Error(secErr.message);
    }
    // Copy visibility
    const { data: vis } = await sb
      .from("category_experiences_v2")
      .select("experience_id")
      .eq("category_id", data.id);
    if (vis && vis.length > 0) {
      await sb
        .from("category_experiences_v2")
        .insert(vis.map((v) => ({ category_id: newId, experience_id: v.experience_id as string })));
    }
    return { ok: true, id: newId };
  });

export const listCategoryVisibilityAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("category_experiences_v2")
      .select("category_id,experience_id");
    if (error) throw new Error(error.message);
    return (data ?? []) as { category_id: string; experience_id: string }[];
  });

export const reorderCategoriesV2 = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => reorderCatsSchema.parse(input))
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    for (let i = 0; i < data.ordered_ids.length; i++) {
      const { error } = await sb
        .from("categories")
        .update({ position: -1000 - i })
        .eq("id", data.ordered_ids[i]);
      if (error) throw new Error(error.message);
    }
    for (let i = 0; i < data.ordered_ids.length; i++) {
      const { error } = await sb
        .from("categories")
        .update({ position: i, updated_at: new Date().toISOString() })
        .eq("id", data.ordered_ids[i]);
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });

const visibilitySchema = z.object({
  category_id: z.string().min(1).max(100),
  experience_ids: z.array(z.string().uuid()).max(50),
});

export const setCategoryVisibility = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => visibilitySchema.parse(input))
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    const { error: delErr } = await sb
      .from("category_experiences_v2")
      .delete()
      .eq("category_id", data.category_id);
    if (delErr) throw new Error(delErr.message);
    if (data.experience_ids.length > 0) {
      const rows = data.experience_ids.map((eid) => ({
        category_id: data.category_id,
        experience_id: eid,
      }));
      const { error: insErr } = await sb.from("category_experiences_v2").insert(rows);
      if (insErr) throw new Error(insErr.message);
    }
    return { ok: true };
  });

// --- Admin: sections ---

export const listSectionsAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ category_id: z.string().min(1).max(100) }).parse(input),
  )
  .handler(async ({ data, context }): Promise<SectionRow[]> => {
    const { data: rows, error } = await context.supabase
      .from("category_sections")
      .select(SEC_COLS)
      .eq("category_id", data.category_id)
      .order("position", { ascending: true });
    if (error) throw new Error(error.message);
    return (rows ?? []) as SectionRow[];
  });

const SECTION_TYPES = [
  "hero",
  "media_text_split",
  "full_bleed_media",
  "gallery",
  "pull_quote",
  "stats",
  "cta",
  "accordion_inline",
] as const;

const addSectionSchema = z.object({
  category_id: z.string().min(1).max(100),
  type: z.enum(SECTION_TYPES),
});

export const addSection = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => addSectionSchema.parse(input))
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    const { data: existing, error: exErr } = await sb
      .from("category_sections")
      .select("position")
      .eq("category_id", data.category_id)
      .order("position", { ascending: false })
      .limit(1);
    if (exErr) throw new Error(exErr.message);
    const nextPos = ((existing?.[0]?.position ?? -1) as number) + 1;
    const { data: inserted, error } = await sb
      .from("category_sections")
      .insert({
        category_id: data.category_id,
        type: data.type,
        position: nextPos,
        data: {},
      })
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { ok: true, id: inserted.id as string };
  });

const updateSectionSchema = z.object({
  id: z.string().uuid(),
  data: z.record(z.string(), z.any()),
  hidden: z.boolean().optional(),
});

export const updateSection = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => updateSectionSchema.parse(input))
  .handler(async ({ data, context }) => {
    const patch: {
      data: any;
      updated_at: string;
      hidden?: boolean;
    } = {
      data: data.data,
      updated_at: new Date().toISOString(),
    };
    if (typeof data.hidden === "boolean") patch.hidden = data.hidden;
    const { error } = await context.supabase
      .from("category_sections")
      .update(patch as any)
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

const deleteSectionSchema = z.object({ id: z.string().uuid() });

export const deleteSection = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => deleteSectionSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("category_sections")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

const reorderSectionsSchema = z.object({
  category_id: z.string().min(1).max(100),
  ordered_ids: z.array(z.string().uuid()).min(1).max(200),
});

export const reorderSections = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => reorderSectionsSchema.parse(input))
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    for (let i = 0; i < data.ordered_ids.length; i++) {
      const { error } = await sb
        .from("category_sections")
        .update({ position: -1000 - i })
        .eq("id", data.ordered_ids[i])
        .eq("category_id", data.category_id);
      if (error) throw new Error(error.message);
    }
    for (let i = 0; i < data.ordered_ids.length; i++) {
      const { error } = await sb
        .from("category_sections")
        .update({ position: i, updated_at: new Date().toISOString() })
        .eq("id", data.ordered_ids[i])
        .eq("category_id", data.category_id);
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });
